'use client';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import Image from 'next/image';

const SOCIAL_LINKS = [
  {
    key: 'discord',
    label: 'Discord',
    href: 'https://discord.gg/tVnb5yjS9K',
    iconSrc: '/social-icons/discord.svg',
    iconWidth: 18,
    iconHeight: 13,
    brandClass: 'hover:bg-[#5865F2]/25',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    href: 'https://www.facebook.com/aifoglobal/',
    iconSrc: '/social-icons/facebook.svg',
    iconWidth: 18,
    iconHeight: 18,
    brandClass: 'hover:bg-[#1877F2]/25',
  },
  {
    key: 'x',
    label: 'X',
    href: 'https://x.com/aifofunding',
    iconSrc: '/social-icons/x.svg',
    iconWidth: 15,
    iconHeight: 14,
    brandClass: 'hover:bg-white/[0.1]',
  },
  {
    key: 'instagram',
    label: 'Instagram',
    href: 'https://www.instagram.com/aifofunding',
    iconSrc: '/social-icons/ig.svg',
    iconWidth: 18,
    iconHeight: 18,
    brandClass: 'hover:bg-[#d62976]/25',
  },
  {
    key: 'youtube',
    label: 'YouTube',
    href: 'https://www.youtube.com/@AIFOcom',
    iconSrc: '/social-icons/youtube.svg',
    iconWidth: 18,
    iconHeight: 13,
    brandClass: 'hover:bg-[#FF0000]/25',
  },
] as const;

type SocialLink = (typeof SOCIAL_LINKS)[number];

function SocialIcon({ social }: { social: SocialLink }) {
  return (
    <Image
      src={social.iconSrc}
      alt=""
      width={social.iconWidth}
      height={social.iconHeight}
      className="max-h-[18px] max-w-[18px] object-contain"
      aria-hidden="true"
    />
  );
}

function SocialLinkButton({ social, className }: { social: SocialLink; className?: string }) {
  return (
    <Button
      asChild
      variant="ghost"
      size="icon-sm"
      className={`size-8 rounded-lg border border-white/[0.1] shadow-none transition-all hover:scale-105 focus-visible:ring-white/25 ${social.brandClass} ${className ?? ''}`}>
      <a
        href={social.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open ${social.label}`}
        title={social.label}>
        <SocialIcon social={social} />
      </a>
    </Button>
  );
}

function SocialLinkMenuItem({ social, className }: { social: SocialLink; className?: string }) {
  return (
    <DropdownMenuItem
      asChild
      className={`cursor-pointer rounded-md px-2.5 py-2 text-white/82 outline-none focus:bg-white/[0.08] focus:text-white ${className ?? ''}`}>
      <a
        href={social.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open ${social.label}`}>
        <span className={`flex size-8 items-center justify-center rounded-lg border border-white/[0.1] ${social.brandClass}`}>
          <SocialIcon social={social} />
        </span>
        <span className="text-sm">{social.label}</span>
      </a>
    </DropdownMenuItem>
  );
}

function MobileSocialLinks() {
  return (
    <div className="flex items-center gap-2 sm:hidden">
      <SocialLinkButton
        social={SOCIAL_LINKS[0]}
        className="hidden min-[480px]:inline-flex"
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            className="size-8 rounded-lg border border-white/[0.1] bg-white/[0.055] text-white/65 transition-colors hover:bg-white/[0.1] hover:text-white focus-visible:ring-white/25"
            aria-label="Open social links"
            title="Social links">
            <MoreHorizontal className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={8}
          className="min-w-40 border-white/[0.1] bg-[#100d0c]/95 p-1.5 text-white shadow-xl backdrop-blur">
          <SocialLinkMenuItem
            social={SOCIAL_LINKS[0]}
            className="min-[480px]:hidden"
          />
          {SOCIAL_LINKS.slice(1).map((social) => (
            <SocialLinkMenuItem
              key={social.key}
              social={social}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function SocialLinks() {
  return (
    <>
      <div className="hidden items-center gap-2 sm:flex">
        {SOCIAL_LINKS.map((social) => (
          <SocialLinkButton
            key={social.key}
            social={social}
          />
        ))}
      </div>
      <MobileSocialLinks />
    </>
  );
}
